import React, { useState, useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import { translate } from '@portal/services/i18n';
import { sendEvent } from '@portal/services/firebase';
import * as StorageService from '~/services/storage';
import { Row } from 'antd';

import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import CompanyIcon from '@portal/assets/svg/onboarding/ic_company.svg?react';
import PersonIcon from '@portal/assets/svg/user_single.svg?react';

import { TaxIdentifierType } from '@portal/enum/onboardingType';
import { EventType } from '@portal/enum/analytics';
import { AccountGroupType } from '@portal/models/enumerators';
import { onboarding, escrow } from '@portal/config/app.json';

import './AccountType.less';
import useReduxState from '@portal/hooks/useReduxState';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { useNavigation } from '@portal/hooks/useNavigation';

const AccountType: React.FC = () => {
  const navigate = useNavigation<{
    accountType: TaxIdentifierType;
    isEscrowOnboarding?: boolean;
    groupType?: AccountGroupType | null;
  }>();
  const {
    theme: { currentTheme },
  } = useReduxState();
  const locationState = navigate?.location?.state;
  const isEscrowOnboarding = locationState?.isEscrowOnboarding;

  const [accountType, setAccountType] = useState(TaxIdentifierType.PF);
  const [groupType, setGroupType] = useState<AccountGroupType | null>();

  const handleAccountType = () => {
    StorageService.clear();

    if (!StorageService.getDeviceId()) {
      StorageService.setItem('device-id', uuid());
    }

    const commonPayload = { accountType };

    if (groupType === AccountGroupType.ESCROW) {
      navigate('/abertura-de-conta', {
        state: {
          ...commonPayload,
          isEscrowOnboarding: true,
          groupType: AccountGroupType.ESCROW,
        },
      });
      return;
    }

    if (
      escrow.onboarding &&
      accountType === TaxIdentifierType.PJ &&
      groupType === undefined
    ) {
      navigate('/tipo-de-conta', {
        state: {
          ...commonPayload,
          isEscrowOnboarding: true,
          groupType: null,
        },
      });
      return;
    }

    sendEvent(EventType.SIGN_UP_START);
    navigate('/abertura-de-conta', { state: commonPayload });
  };

  const getButtonClass = (selected: boolean) =>
    `account-type__option ${selected ? 'account-type__option--selected' : ''}`;

  const buttons = useMemo(
    () =>
      [
        {
          visible: onboarding.allowPerson,
          title: translate(
            `PAGES.AUTH.ACCOUNT_TYPE.${isEscrowOnboarding ? 'STANDARD' : 'PF'}`
          ),
          description: translate('PAGES.AUTH.ACCOUNT_TYPE.PF_DESCRIPTION'),
          icon: <PersonIcon />,
          selected: !isEscrowOnboarding
            ? accountType === TaxIdentifierType.PF
            : groupType === null,
          onClick: () =>
            isEscrowOnboarding
              ? setGroupType(null)
              : setAccountType(TaxIdentifierType.PF),
        },
        {
          visible: onboarding.allowLegalPerson,
          title: translate(
            `PAGES.AUTH.ACCOUNT_TYPE.${isEscrowOnboarding ? 'ESCROW' : 'PJ'}`
          ),
          description: translate('PAGES.AUTH.ACCOUNT_TYPE.PJ_DESCRIPTION'),
          icon: <CompanyIcon />,
          selected: !isEscrowOnboarding
            ? accountType === TaxIdentifierType.PJ
            : groupType === AccountGroupType.ESCROW,
          onClick: () =>
            isEscrowOnboarding
              ? setGroupType(AccountGroupType.ESCROW)
              : setAccountType(TaxIdentifierType.PJ),
        },
      ].filter((b) => b.visible),
    [accountType, groupType, isEscrowOnboarding]
  );

  const handleLogin = () => {
    navigate('/');
  };

  return (
    <>
      <div className="account-type__already-account">
        <ThemedText>
          {translate('PAGES.AUTH.ACCOUNT_TYPE.ALREADY_HAVE_ACCOUNT')}
        </ThemedText>
        <button className="transparent-button" onClick={handleLogin}>
          <ThemedText color="primary" fontWeight="bold">
            {translate('PAGES.AUTH.ACCOUNT_TYPE.LOGIN')}
          </ThemedText>
        </button>
      </div>
      <div className="account-type">
        <div
          className="account-type__section"
          style={{ borderTop: `4px solid ${currentTheme.borderColor}` }}
        >
          <ThemedText variant="h1">
            {translate('PAGES.AUTH.ACCOUNT_TYPE.PAGE_TITLE')}
          </ThemedText>
          <ThemedText variant="body">
            {translate(
              `PAGES.AUTH.ACCOUNT_TYPE.${
                isEscrowOnboarding ? 'ESCROW_DESCRIPTION' : 'PAGE_DESCRIPTION'
              }`
            )}
          </ThemedText>
          <div className="account-type__grid">
            {buttons.map((button) => (
              <button
                key={button.title}
                onClick={button.onClick}
                className={getButtonClass(button.selected)}
                style={{
                  background: button.selected
                    ? currentTheme.background
                    : 'transparent',
                }}
              >
                <div className="account-type__icon">{button.icon}</div>
                <ThemedText
                  variant="h3"
                  color={button.selected ? 'white' : 'default'}
                >
                  {button.title}
                </ThemedText>
                <ThemedText color={button.selected ? 'white' : 'darkGray'}>
                  {button.description}
                </ThemedText>
              </button>
            ))}
          </div>
          <Row justify="center">
            <AdvancedButton
              text={translate('PAGES.AUTH.ACCOUNT_TYPE.CONTINUE')}
              onClick={handleAccountType}
            />
          </Row>
        </div>
      </div>
    </>
  );
};

export default AccountType;
